import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { getDisplayName, STATUS_LABEL, TYP_LABEL } from '../lib/kunden-display';
import type { KundeStatus, KundeTyp, SlaStatus } from '../lib/api-enums';
import { formatGermanDate, formatGermanDateFromTimestamp, msUntil } from '../lib/date-format';

interface Kunde {
  id: string;
  kundennummer: string;
  typ: KundeTyp;
  status: KundeStatus;
  firmenname: string | null;
  umsatzsteuerId: string | null;
  steuernummer: string | null;
  handelsregister: string | null;
  rechtsform: string | null;
  kategorie: string | null;
  quelle: string | null;
  anrede: string | null;
  titel: string | null;
  vorname: string | null;
  nachname: string | null;
  geburtsdatum: string | null;
  bankIban: string | null;
  bankBic: string | null;
  bankKontoinhaber: string | null;
  zahlungszielTage: number;
  skontoProzent: string | null;
  skontoTage: number | null;
  notizen: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Objekt {
  id: string;
  kundeId: string;
  objektnummer: string | null;
  bezeichnung: string;
  adresseStrasse: string | null;
  adressePlz: string | null;
  adresseOrt: string | null;
  typ: string | null;
  anzahlEinheiten: number | null;
}

interface Kontakt {
  id: string;
  kundeId: string;
  vorname: string | null;
  nachname: string;
  rolle: string | null;
  email: string | null;
  telefon: string | null;
  mobil: string | null;
  istHauptkontakt: boolean;
}

interface Anfrage {
  id: string;
  eingangAm: string;
  quelle: string;
  betreff: string | null;
  anfrageTyp: string | null;
  status: string;
  slaStatus: SlaStatus | null;
  slaReaktionBis: string | null;
}

export const Route = createFileRoute('/kunden/$id')({
  beforeLoad: () => requireAccess('/api/v1/kunden'),
  component: KundeDetailPage,
});

function KundeDetailPage() {
  const { id } = Route.useParams();
  const kundeQ = useQuery({
    queryKey: ['kunde', id],
    queryFn: async () => {
      try {
        return await api<{ item: Kunde }>(`/api/v1/kunden/${id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', id],
    queryFn: () => api<{ items: Objekt[] }>(`/api/v1/objekte?kundeId=${id}`),
    enabled: kundeQ.data != null,
  });
  const kontakteQ = useQuery({
    queryKey: ['kontakte', id],
    queryFn: () => api<{ items: Kontakt[] }>(`/api/v1/kontakte?kundeId=${id}`),
    enabled: kundeQ.data != null,
  });
  const anfragenQ = useQuery({
    queryKey: ['inbox', 'kunde', id],
    queryFn: () => api<{ items: Anfrage[] }>(`/api/v1/inbox?kundeId=${id}&limit=50`),
    enabled: kundeQ.data != null,
  });

  if (kundeQ.isLoading) return <div className="text-slate-500">Lade…</div>;
  if (kundeQ.error) return <ErrorBanner error={kundeQ.error} />;
  if (!kundeQ.data) {
    return (
      <div className="space-y-4">
        <Link to="/kunden" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Kundenliste
        </Link>
        <div className="rounded-md border border-slate-200 bg-white p-6 text-slate-500">
          Kunde nicht gefunden.
        </div>
      </div>
    );
  }
  const kunde = kundeQ.data.item;
  const display = getDisplayName(kunde) || kunde.kundennummer;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/kunden" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Kundenliste
        </Link>
      </div>
      <PageHeader
        title={display}
        description={`Kundennummer ${kunde.kundennummer} · ${TYP_LABEL[kunde.typ] ?? kunde.typ} · ${STATUS_LABEL[kunde.status] ?? kunde.status}`}
      />

      <KundeStammdatenPanel kunde={kunde} />
      <KundeBankPanel kunde={kunde} />

      <ObjektePanel
        kundeId={id}
        objekte={objekteQ.data?.items ?? []}
        error={objekteQ.error}
        loading={objekteQ.isLoading}
      />

      <KontaktePanel
        kundeId={id}
        kontakte={kontakteQ.data?.items ?? []}
        error={kontakteQ.error}
        loading={kontakteQ.isLoading}
      />

      <AnfragenPanel
        anfragen={anfragenQ.data?.items ?? []}
        loading={anfragenQ.isLoading}
        error={anfragenQ.error}
      />
    </div>
  );
}

function AnfragenPanel({
  anfragen,
  loading,
  error,
}: {
  anfragen: Anfrage[];
  loading: boolean;
  error: unknown;
}) {
  const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recent = anfragen.filter((a) => new Date(a.eingangAm).getTime() >= cutoff);
  const verletzt30d = recent.filter((a) => a.slaStatus === 'verletzt').length;
  return (
    <Panel title="Anfragen / Inbox">
      <div className="px-4 py-3">
        {error ? (
          <ErrorBanner error={error} />
        ) : loading ? (
          <div className="text-sm text-slate-500">lädt …</div>
        ) : anfragen.length === 0 ? (
          <div className="text-sm italic text-slate-500">Keine Anfragen für diesen Kunden.</div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
              <span>
                <strong className="text-slate-900">{recent.length}</strong> in den letzten 30 Tagen
              </span>
              {verletzt30d > 0 ? (
                <span className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-red-700">
                  ⚠ {verletzt30d} SLA verletzt
                </span>
              ) : null}
              <span className="ml-auto text-slate-400">
                Gesamt: {anfragen.length} {anfragen.length === 50 ? '(letzte 50)' : ''}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs font-medium text-slate-600">
                <tr className="border-b border-slate-200">
                  <th className="py-1.5 text-left">Eingang</th>
                  <th className="py-1.5 text-left">Quelle</th>
                  <th className="py-1.5 text-left">Betreff</th>
                  <th className="py-1.5 text-left">Typ</th>
                  <th className="py-1.5 text-left">SLA</th>
                  <th className="py-1.5 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {anfragen.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-1.5 font-mono text-xs text-slate-600">
                      <Link
                        to="/inbox/$id"
                        params={{ id: a.id }}
                        className="text-brand-700 hover:underline"
                      >
                        {formatGermanDateFromTimestamp(a.eingangAm)}
                      </Link>
                    </td>
                    <td className="py-1.5 text-xs text-slate-700">{a.quelle}</td>
                    <td className="py-1.5">
                      <Link to="/inbox/$id" params={{ id: a.id }} className="hover:underline">
                        {a.betreff ?? <span className="italic text-slate-400">—</span>}
                      </Link>
                    </td>
                    <td className="py-1.5 text-slate-700">{a.anfrageTyp ?? '—'}</td>
                    <td className="py-1.5">
                      <SlaDot anfrage={a} />
                    </td>
                    <td className="py-1.5 text-xs text-slate-600">{a.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Panel>
  );
}

function SlaDot({ anfrage }: { anfrage: Anfrage }) {
  if (!anfrage.slaStatus) return <span className="text-xs text-slate-400">—</span>;
  if (anfrage.slaStatus === 'verletzt') return <span className="text-red-600">●</span>;
  if (anfrage.slaStatus === 'erfuellt') return <span className="text-green-600">●</span>;
  if (anfrage.slaReaktionBis) {
    const restMs = msUntil(anfrage.slaReaktionBis);
    if (restMs <= 0) return <span className="text-red-600">●</span>;
  }
  return <span className="text-amber-600">●</span>;
}

function KundeStammdatenPanel({ kunde }: { kunde: Kunde }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  // Firma + Privat teilen sich das Edit-State; das jeweils irrelevante Feld
  // bleibt unverändert leer/befüllt.
  const [firmenname, setFirmenname] = useState(kunde.firmenname ?? '');
  const [umsatzsteuerId, setUmsatzsteuerId] = useState(kunde.umsatzsteuerId ?? '');
  const [steuernummer, setSteuernummer] = useState(kunde.steuernummer ?? '');
  const [handelsregister, setHandelsregister] = useState(kunde.handelsregister ?? '');
  const [rechtsform, setRechtsform] = useState(kunde.rechtsform ?? '');
  const [anrede, setAnrede] = useState(kunde.anrede ?? '');
  const [titel, setTitel] = useState(kunde.titel ?? '');
  const [vorname, setVorname] = useState(kunde.vorname ?? '');
  const [nachname, setNachname] = useState(kunde.nachname ?? '');
  const [geburtsdatum, setGeburtsdatum] = useState(kunde.geburtsdatum ?? '');
  const [kategorie, setKategorie] = useState(kunde.kategorie ?? '');
  const [quelle, setQuelle] = useState(kunde.quelle ?? '');
  const [notizen, setNotizen] = useState(kunde.notizen ?? '');
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, string> = {};
      const cmp = (db: string | null, ui: string) => (db ?? '') !== ui;
      if (kunde.typ === 'firma') {
        if (cmp(kunde.firmenname, firmenname)) patch['firmenname'] = firmenname;
        if (cmp(kunde.umsatzsteuerId, umsatzsteuerId)) patch['umsatzsteuerId'] = umsatzsteuerId;
        if (cmp(kunde.steuernummer, steuernummer)) patch['steuernummer'] = steuernummer;
        if (cmp(kunde.handelsregister, handelsregister)) patch['handelsregister'] = handelsregister;
        if (cmp(kunde.rechtsform, rechtsform)) patch['rechtsform'] = rechtsform;
      } else {
        if (cmp(kunde.anrede, anrede)) patch['anrede'] = anrede;
        if (cmp(kunde.titel, titel)) patch['titel'] = titel;
        if (cmp(kunde.vorname, vorname)) patch['vorname'] = vorname;
        if (cmp(kunde.nachname, nachname)) patch['nachname'] = nachname;
        if (cmp(kunde.geburtsdatum, geburtsdatum)) patch['geburtsdatum'] = geburtsdatum;
      }
      if (cmp(kunde.kategorie, kategorie)) patch['kategorie'] = kategorie;
      if (cmp(kunde.quelle, quelle)) patch['quelle'] = quelle;
      if (cmp(kunde.notizen, notizen)) patch['notizen'] = notizen;
      if (Object.keys(patch).length === 0) return kunde;
      return api<{ item: Kunde }>(`/api/v1/kunden/${kunde.id}`, { method: 'PATCH', body: patch });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['kunde', kunde.id] });
      void qc.invalidateQueries({ queryKey: ['kunden'] });
    },
    onError: (err) => setError(err),
  });

  return (
    <Panel
      title="Stammdaten"
      actions={
        !editing ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setFirmenname(kunde.firmenname ?? '');
                setUmsatzsteuerId(kunde.umsatzsteuerId ?? '');
                setSteuernummer(kunde.steuernummer ?? '');
                setHandelsregister(kunde.handelsregister ?? '');
                setRechtsform(kunde.rechtsform ?? '');
                setAnrede(kunde.anrede ?? '');
                setTitel(kunde.titel ?? '');
                setVorname(kunde.vorname ?? '');
                setNachname(kunde.nachname ?? '');
                setGeburtsdatum(kunde.geburtsdatum ?? '');
                setKategorie(kunde.kategorie ?? '');
                setQuelle(kunde.quelle ?? '');
                setNotizen(kunde.notizen ?? '');
                setError(null);
                setEditing(false);
              }}
              disabled={save.isPending}
            >
              Abbrechen
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        )
      }
    >
      <ErrorBanner error={error} />
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Kundennummer">
          <span className="font-mono text-slate-600">{kunde.kundennummer}</span>
        </Field>
        <Field label="Typ">
          <span className="text-slate-700">{TYP_LABEL[kunde.typ] ?? kunde.typ}</span>
        </Field>

        {kunde.typ === 'firma' ? (
          <>
            <Field label="Firmenname">
              {editing ? (
                <Input value={firmenname} onChange={(e) => setFirmenname(e.target.value)} />
              ) : (
                <span>{kunde.firmenname ?? '–'}</span>
              )}
            </Field>
            <Field label="USt-ID">
              {editing ? (
                <Input
                  value={umsatzsteuerId}
                  onChange={(e) => setUmsatzsteuerId(e.target.value)}
                  placeholder="DE123456789"
                />
              ) : (
                <span className="text-slate-600">{kunde.umsatzsteuerId ?? '–'}</span>
              )}
            </Field>
            <Field label="Steuernummer">
              {editing ? (
                <Input value={steuernummer} onChange={(e) => setSteuernummer(e.target.value)} />
              ) : (
                <span className="text-slate-600">{kunde.steuernummer ?? '–'}</span>
              )}
            </Field>
            <Field label="Handelsregister">
              {editing ? (
                <Input
                  value={handelsregister}
                  onChange={(e) => setHandelsregister(e.target.value)}
                />
              ) : (
                <span className="text-slate-600">{kunde.handelsregister ?? '–'}</span>
              )}
            </Field>
            <Field label="Rechtsform">
              {editing ? (
                <Input value={rechtsform} onChange={(e) => setRechtsform(e.target.value)} />
              ) : (
                <span className="text-slate-600">{kunde.rechtsform ?? '–'}</span>
              )}
            </Field>
          </>
        ) : (
          <>
            <Field label="Anrede">
              {editing ? (
                <Select value={anrede} onChange={(e) => setAnrede(e.target.value)}>
                  <option value="">–</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                  <option value="Divers">Divers</option>
                </Select>
              ) : (
                <span className="text-slate-600">{kunde.anrede ?? '–'}</span>
              )}
            </Field>
            <Field label="Titel">
              {editing ? (
                <Input value={titel} onChange={(e) => setTitel(e.target.value)} />
              ) : (
                <span className="text-slate-600">{kunde.titel ?? '–'}</span>
              )}
            </Field>
            <Field label="Vorname">
              {editing ? (
                <Input value={vorname} onChange={(e) => setVorname(e.target.value)} />
              ) : (
                <span className="text-slate-600">{kunde.vorname ?? '–'}</span>
              )}
            </Field>
            <Field label="Nachname">
              {editing ? (
                <Input value={nachname} onChange={(e) => setNachname(e.target.value)} />
              ) : (
                <span>{kunde.nachname ?? '–'}</span>
              )}
            </Field>
            <Field label="Geburtsdatum">
              {editing ? (
                <Input
                  type="date"
                  value={geburtsdatum}
                  onChange={(e) => setGeburtsdatum(e.target.value)}
                />
              ) : (
                <span className="text-slate-600">
                  {kunde.geburtsdatum ? formatGermanDate(kunde.geburtsdatum) : '–'}
                </span>
              )}
            </Field>
          </>
        )}

        <Field label="Kategorie">
          {editing ? (
            <Input value={kategorie} onChange={(e) => setKategorie(e.target.value)} />
          ) : (
            <span className="text-slate-600">{kunde.kategorie ?? '–'}</span>
          )}
        </Field>
        <Field label="Quelle">
          {editing ? (
            <Input value={quelle} onChange={(e) => setQuelle(e.target.value)} />
          ) : (
            <span className="text-slate-600">{kunde.quelle ?? '–'}</span>
          )}
        </Field>
        <Field label="Angelegt">
          <span className="text-slate-500">{formatGermanDateFromTimestamp(kunde.createdAt)}</span>
        </Field>
        <div className="md:col-span-2">
          <Field label="Notizen">
            {editing ? (
              <textarea
                value={notizen}
                onChange={(e) => setNotizen(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            ) : (
              <span className="text-slate-600 whitespace-pre-wrap">{kunde.notizen ?? '–'}</span>
            )}
          </Field>
        </div>
      </dl>
    </Panel>
  );
}

function KundeBankPanel({ kunde }: { kunde: Kunde }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [bankIban, setBankIban] = useState(kunde.bankIban ?? '');
  const [bankBic, setBankBic] = useState(kunde.bankBic ?? '');
  const [bankKontoinhaber, setBankKontoinhaber] = useState(kunde.bankKontoinhaber ?? '');
  const [zahlungszielTage, setZahlungszielTage] = useState(kunde.zahlungszielTage);
  const [skontoProzent, setSkontoProzent] = useState(kunde.skontoProzent ?? '');
  const [skontoTage, setSkontoTage] = useState<string>(
    kunde.skontoTage != null ? String(kunde.skontoTage) : '',
  );
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, string | number> = {};
      const cmpStr = (db: string | null, ui: string) => (db ?? '') !== ui;
      if (cmpStr(kunde.bankIban, bankIban)) patch['bankIban'] = bankIban;
      if (cmpStr(kunde.bankBic, bankBic)) patch['bankBic'] = bankBic;
      if (cmpStr(kunde.bankKontoinhaber, bankKontoinhaber))
        patch['bankKontoinhaber'] = bankKontoinhaber;
      if (zahlungszielTage !== kunde.zahlungszielTage) patch['zahlungszielTage'] = zahlungszielTage;
      if (cmpStr(kunde.skontoProzent, skontoProzent)) patch['skontoProzent'] = skontoProzent;
      const skontoTageNum = skontoTage.trim() ? Number(skontoTage) : null;
      const skontoTageDb = kunde.skontoTage;
      if (skontoTageNum !== skontoTageDb) {
        if (skontoTageNum != null) patch['skontoTage'] = skontoTageNum;
      }
      if (Object.keys(patch).length === 0) return kunde;
      return api<{ item: Kunde }>(`/api/v1/kunden/${kunde.id}`, { method: 'PATCH', body: patch });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['kunde', kunde.id] });
      void qc.invalidateQueries({ queryKey: ['kunden'] });
    },
    onError: (err) => setError(err),
  });

  return (
    <Panel
      title="Bank & Zahlung"
      actions={
        !editing ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setBankIban(kunde.bankIban ?? '');
                setBankBic(kunde.bankBic ?? '');
                setBankKontoinhaber(kunde.bankKontoinhaber ?? '');
                setZahlungszielTage(kunde.zahlungszielTage);
                setSkontoProzent(kunde.skontoProzent ?? '');
                setSkontoTage(kunde.skontoTage != null ? String(kunde.skontoTage) : '');
                setError(null);
                setEditing(false);
              }}
              disabled={save.isPending}
            >
              Abbrechen
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        )
      }
    >
      <ErrorBanner error={error} />
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="IBAN" wide>
          {editing ? (
            <Input value={bankIban} onChange={(e) => setBankIban(e.target.value)} />
          ) : (
            <span className="font-mono text-slate-600">{kunde.bankIban ?? '–'}</span>
          )}
        </Field>
        <Field label="BIC">
          {editing ? (
            <Input value={bankBic} onChange={(e) => setBankBic(e.target.value)} />
          ) : (
            <span className="text-slate-600">{kunde.bankBic ?? '–'}</span>
          )}
        </Field>
        <Field label="Kontoinhaber">
          {editing ? (
            <Input value={bankKontoinhaber} onChange={(e) => setBankKontoinhaber(e.target.value)} />
          ) : (
            <span className="text-slate-600">{kunde.bankKontoinhaber ?? '–'}</span>
          )}
        </Field>
        <Field label="Zahlungsziel (Tage)">
          {editing ? (
            <Input
              type="number"
              min={0}
              max={365}
              value={zahlungszielTage}
              onChange={(e) => setZahlungszielTage(Number(e.target.value))}
            />
          ) : (
            <span className="text-slate-600">{kunde.zahlungszielTage}</span>
          )}
        </Field>
        <Field label="Skonto %">
          {editing ? (
            <Input
              type="number"
              step="0.01"
              min={0}
              value={skontoProzent}
              onChange={(e) => setSkontoProzent(e.target.value)}
            />
          ) : (
            <span className="text-slate-600">{kunde.skontoProzent ?? '–'}</span>
          )}
        </Field>
        <Field label="Skonto-Frist (Tage)">
          {editing ? (
            <Input
              type="number"
              min={0}
              value={skontoTage}
              onChange={(e) => setSkontoTage(e.target.value)}
            />
          ) : (
            <span className="text-slate-600">{kunde.skontoTage ?? '–'}</span>
          )}
        </Field>
      </dl>
    </Panel>
  );
}

function ObjektePanel({
  kundeId,
  objekte,
  error,
  loading,
}: {
  kundeId: string;
  objekte: Objekt[];
  error: unknown;
  loading: boolean;
}) {
  return (
    <Panel
      title={`Objekte (${objekte.length})`}
      actions={
        <Link
          to="/objekte"
          search={{ kundeId } as Record<string, string>}
          className="text-sm text-brand-700 hover:underline"
        >
          + Objekt anlegen
        </Link>
      }
    >
      {loading && <div className="text-slate-500 text-sm">Lade…</div>}
      <ErrorBanner error={error} />
      {objekte.length === 0 && !loading && (
        <div className="text-slate-400 text-sm">Keine Objekte fuer diesen Kunden.</div>
      )}
      {objekte.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr className="border-b border-slate-100">
              <th className="py-1.5 text-left">Nr.</th>
              <th className="py-1.5 text-left">Bezeichnung</th>
              <th className="py-1.5 text-left">Adresse</th>
              <th className="py-1.5 text-left">Typ</th>
              <th className="py-1.5 text-right">Einheiten</th>
            </tr>
          </thead>
          <tbody>
            {objekte.map((o) => (
              <tr key={o.id} className="border-b border-slate-50 last:border-0">
                <td className="py-1.5 font-mono text-xs text-slate-600">{o.objektnummer ?? '–'}</td>
                <td className="py-1.5 font-medium">{o.bezeichnung}</td>
                <td className="py-1.5 text-slate-600">
                  {[o.adresseStrasse, o.adressePlz, o.adresseOrt].filter(Boolean).join(', ') || '–'}
                </td>
                <td className="py-1.5 text-slate-600">{o.typ ?? '–'}</td>
                <td className="py-1.5 text-right tabular-nums">{o.anzahlEinheiten ?? '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function KontaktePanel({
  kundeId,
  kontakte,
  error,
  loading,
}: {
  kundeId: string;
  kontakte: Kontakt[];
  error: unknown;
  loading: boolean;
}) {
  const sorted = [...kontakte].sort((a, b) => {
    if (a.istHauptkontakt !== b.istHauptkontakt) return a.istHauptkontakt ? -1 : 1;
    return a.nachname.localeCompare(b.nachname);
  });
  return (
    <Panel
      title={`Kontakte (${kontakte.length})`}
      actions={
        <Link
          to="/kontakte"
          search={{ kundeId } as Record<string, string>}
          className="text-sm text-brand-700 hover:underline"
        >
          + Kontakt anlegen
        </Link>
      }
    >
      {loading && <div className="text-slate-500 text-sm">Lade…</div>}
      <ErrorBanner error={error} />
      {sorted.length === 0 && !loading && (
        <div className="text-slate-400 text-sm">Keine Kontakte fuer diesen Kunden.</div>
      )}
      {sorted.length > 0 && (
        <ul className="space-y-2 text-sm">
          {sorted.map((k) => (
            <li
              key={k.id}
              className="flex items-start justify-between rounded-md border border-slate-100 bg-slate-50/50 px-3 py-2"
            >
              <div>
                <div className="font-medium">
                  {[k.vorname, k.nachname].filter(Boolean).join(' ')}
                  {k.istHauptkontakt && (
                    <span className="ml-2 inline-flex items-center rounded-md border bg-amber-100 text-amber-800 border-amber-200 px-1.5 py-0.5 text-xs font-medium">
                      Haupt
                    </span>
                  )}
                </div>
                {k.rolle && <div className="text-xs text-slate-500">{k.rolle}</div>}
              </div>
              <div className="text-xs text-slate-600 text-right">
                {k.email && <div>{k.email}</div>}
                {k.telefon && <div>{k.telefon}</div>}
                {k.mobil && <div>{k.mobil}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Panel({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {actions}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
